import React from 'react';
import { CreditCard, Calendar, Percent, DollarSign, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент формы для ввода данных о текущем кредите
 */
const CurrentLoanSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  calculateCurrentPayment
}) => {
  return (
    <div className="mb-6 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/10 dark:to-red-900/10 p-5 rounded-lg border border-orange-200 dark:border-orange-800/50 shadow-sm">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-orange-500" />
        Текущий кредит
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Остаток основного долга */}
        <div>
          <label htmlFor="currentLoanAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <DollarSign className="w-4 h-4 mr-1 text-orange-500" />
            Остаток по кредиту
          </label>
          <input
            type="text"
            id="currentLoanAmount"
            name="currentLoanAmount"
            value={formData.currentLoanAmount}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.currentLoanAmount ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          {errors.currentLoanAmount && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.currentLoanAmount}
            </p>
          )}
        </div>

        {/* Текущая процентная ставка */}
        <div>
          <label htmlFor="currentInterestRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Percent className="w-4 h-4 mr-1 text-orange-500" />
            Текущая ставка (%)
          </label>
          <input
            type="text"
            id="currentInterestRate"
            name="currentInterestRate"
            value={formData.currentInterestRate}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.currentInterestRate ? 'input-error' : ''}`}
            placeholder="Введите ставку"
          />
          {errors.currentInterestRate && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.currentInterestRate}
            </p>
          )}
        </div>

        {/* Оставшийся срок кредита */}
        <div>
          <label htmlFor="currentRemainingTerm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Calendar className="w-4 h-4 mr-1 text-orange-500" />
            Оставшийся срок (лет)
          </label>
          <input
            type="text"
            id="currentRemainingTerm"
            name="currentRemainingTerm"
            value={formData.currentRemainingTerm}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.currentRemainingTerm ? 'input-error' : ''}`}
            placeholder="Введите срок"
          />
          {errors.currentRemainingTerm && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.currentRemainingTerm}
            </p>
          )}
        </div>

        {/* Текущий платеж */}
        <div className="relative">
          <label htmlFor="currentMonthlyPayment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <DollarSign className="w-4 h-4 mr-1 text-orange-500" />
            Текущий ежемесячный платеж
          </label>
          <div className="flex">
            <input
              type="text"
              id="currentMonthlyPayment"
              name="currentMonthlyPayment"
              value={formData.currentMonthlyPayment}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.currentMonthlyPayment ? 'input-error' : ''}`}
              placeholder="Введите платеж или рассчитайте"
            />
            <Button
              type="button"
              onClick={calculateCurrentPayment}
              size="sm"
              variant="outline"
              className="ml-2 whitespace-nowrap"
              title="Рассчитать ежемесячный платеж"
              icon={<Calculator className="w-4 h-4" />}
            >
              Рассчитать
            </Button>
          </div>
          {errors.currentMonthlyPayment && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.currentMonthlyPayment}
            </p>
          )}
        </div>

        {/* Уже выплаченная сумма */}
        <div>
          <label htmlFor="alreadyPaid" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <DollarSign className="w-4 h-4 mr-1 text-orange-500" />
            Уже выплачено по кредиту
          </label>
          <input
            type="text"
            id="alreadyPaid"
            name="alreadyPaid"
            value={formData.alreadyPaid}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.alreadyPaid ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          {errors.alreadyPaid && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.alreadyPaid}
            </p>
          )}
        </div>

        {/* Тип платежа */}
        <div>
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Тип платежа
          </span>
          <div className="grid grid-cols-2 gap-2">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="currentPaymentType"
                value="annuity"
                checked={formData.currentPaymentType === 'annuity'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Аннуитетный (равные платежи)
              </span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="currentPaymentType"
                value="differentiated"
                checked={formData.currentPaymentType === 'differentiated'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Дифференцированный (уменьшающийся)
              </span>
            </label>
          </div>
          {errors.currentPaymentType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.currentPaymentType}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 text-sm text-orange-700 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/30 p-3 rounded-lg border border-orange-200 dark:border-orange-700">
        <div className="flex items-start">
          <CreditCard className="w-5 h-5 text-orange-600 dark:text-orange-400 mr-2 mt-0.5" />
          <div>
            <p className="font-medium">Важно указать точную сумму остатка!</p>
            <p className="text-xs mt-1">Узнать актуальный остаток по кредиту можно в личном кабинете банка, в мобильном приложении или позвонив на горячую линию.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrentLoanSection;